
import { useEffect } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import FloatingLineButton from '../home/components/FloatingLineButton';
import FAQList from './components/FAQList';

const FAQ_SCHEMA_ITEMS = [
  { question: '登録や相談に費用はかかりますか?', answer: '一切かかりません。登録、相談、職場紹介まですべて無料です。無理な営業や、ご連絡も一切しませんのでご安心してご利用ください。' },
  { question: '登録にはどのくらい時間がかかりますか?', answer: 'LINEでのご登録は最短5分で完了します。その後、担当者からご連絡させていただき、ご希望の条件をお伺いいたします。' },
  { question: '週3日だけでも本当に働けますか?', answer: 'はい、可能です。週3日、1日6時間からの勤務も多数ご用意しています。実際に60代の女性の方でも、週3日の出勤で毎月16万円の収入に繋がっています。' },
  { question: 'ブランクが5年以上ありますが、大丈夫でしょうか?', answer: 'もちろん大丈夫です。当社の登録スタッフの40%がブランクからの復帰です。事前面談や職場見学を通じて、安心して復帰できる職場をご案内いたします。' },
  { question: '夜勤なしの求人はありますか?', answer: 'はい、日勤のみの求人も多数ございます。ご希望の勤務時間帯をお伝えいただければ、条件に合った求人をご紹介いたします。' },
  { question: '扶養内で働きたいのですが可能ですか?', answer: 'はい、可能です。扶養内でのお仕事も多数ご用意しております。年収の上限に合わせた勤務プランをご提案いたします。' },
  { question: '派遣で働くのが初めてです。どんな仕組みですか?', answer: '派遣の仕組みは、派遣元(トラスト)が雇用主となり、派遣先(施設)にあなたを派遣します。給与や福利厚生は派遣元が担当し、派遣先では施設の指示を受ける形です。' },
  { question: '社会保険には加入できますか?', answer: 'はい、一定の条件を満たせば社会保険(健康保険・厚生年金)に加入いただけます。詳しくは担当者にお気軽にご相談ください。' },
  { question: '交通費は支給されますか?', answer: 'はい、交通費は別途支給いたします。通勤手段やルートに応じて、実費を支給させていただきます。' },
  { question: 'LINE での相談はどのくらいで返信がありますか?', answer: '平日9:00-18:00の間でしたら、通常1時間以内にお返事いたします。土日祝日も営業しておりますので、お気軽にお問合せください。' },
  { question: '就業後のサポートはありますか?', answer: 'はい、就業後も担当者が定期的にフォローいたします。職場での悩みや困りごとがあれば、いつでもご相談ください。迅速に対応いたします。' },
  { question: '正社員への転換は可能ですか?', answer: 'はい、派遣先での実績や評価に応じて、正社員登用の道もございます。キャリアアップを目指す方もぜひご相談ください。' },
];

export default function FAQPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://kaigo.trust-job.com';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // meta tags
    document.title = '介護派遣よくある質問｜費用・働き方・ブランクOK｜トラスト名古屋';
    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.content = content;
    };
    setMeta('description', '名古屋の介護派遣トラストのよくある質問ページ。費用・登録方法・ブランクOK・週3日勤務・扶養内勤務・社会保険など、介護派遣に関するご質問にお答えします。');
    setMeta('keywords', '介護派遣,よくある質問,名古屋,トラスト,ブランクOK,週3日');
    setMeta('og:title', '介護派遣よくある質問｜費用・働き方・ブランクOK｜トラスト名古屋', true);
    setMeta('og:description', '名古屋の介護派遣トラストのよくある質問。費用・登録・ブランク復帰・週3日勤務など介護派遣に関するご質問にお答えします。', true);
    setMeta('og:url', `${siteUrl}/faq`, true);

    // Schema.org
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ_SCHEMA_ITEMS.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    };

    document.querySelectorAll('script[data-schema="faq"]').forEach((s) => s.remove());
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.schema = 'faq';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.querySelectorAll('script[data-schema="faq"]').forEach((s) => s.remove());
    };
  }, [siteUrl]);

  return (
    <div className="bg-gray-50 min-h-screen">
      <Header />
      <main className="pt-14">
        <section className="py-12 sm:py-16 bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-3xl sm:text-4xl font-bold mb-4">
              よくあるご質問
            </h1>
            <p className="text-lg sm:text-xl opacity-90">
              皆さまからよくいただくご質問にお答えします
            </p>
          </div>
        </section>
        <FAQList />
        <section className="py-12 sm:py-16 bg-gradient-to-br from-[#06C755] to-[#05b34c] text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              他にもご不明な点がございましたら
            </h2>
            <p className="text-base sm:text-lg mb-6 opacity-90">
              お気軽にLINEまたはお電話でお問い合わせください
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="https://lin.ee/MJ3LVM2"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="bg-white hover:bg-gray-100 text-[#06C755] px-6 sm:px-8 py-3 sm:py-4 rounded-full flex items-center space-x-3 text-base sm:text-lg font-medium transition-all whitespace-nowrap cursor-pointer shadow-lg"
              >
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-line-fill text-xl"></i>
                </div>
                <span>LINE で気軽に相談する</span>
              </a>
              <a
                href="tel:052-887-6243"
                className="bg-white text-[#06C755] px-6 sm:px-8 py-3 sm:py-4 rounded-full flex items-center space-x-3 text-base sm:text-lg font-medium hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer shadow-lg"
              >
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-phone-line text-xl"></i>
                </div>
                <span>052-887-6243</span>
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <FloatingLineButton />
    </div>
  );
}